// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { authService } from '@/services/api';


// Import views
import Dashboard from '@/views/Dashboard.vue';
import Login from '@/views/Login.vue';
import Inventory from '@/views/Inventory.vue';


// Import patient views
import PatientList from '@/views/patients/PatientList.vue';
import PatientDetail from '@/views/patients/PatientDetail.vue';
import PatientForm from '@/views/patients/PatientForm.vue';

// Import appointment views
import AppointmentList from '@/views/appointments/AppointmentList.vue';
import AppointmentDetail from '@/views/appointments/AppointmentDetail.vue';
import AppointmentForm from '@/views/appointments/AppointmentForm.vue';

// Import clinical history views
import ClinicalHistoryList from '@/views/clinical/ClinicalHistoryList.vue';
import ClinicalHistoryDetail from '@/views/clinical/ClinicalHistoryDetail.vue';
import ClinicalHistoryForm from '@/views/clinical/ClinicalHistoryForm.vue';

// Import odontogram views
import OdontogramDetail from '@/views/odontogram/OdontogramDetail.vue';
import OdontogramForm from '@/views/odontogram/OdontogramForm.vue';

// Import ClinicalExam views
import ClinicalExamForm from '@/views/clinical/ClinicalExamForm.vue'
import ClinicalExamDetail from '@/views/clinical/ClinicalExamDetail.vue'

import TreatmentPlanList from '@/views/treatment-plans/TreatmentPlanList.vue';
import TreatmentPlanDetail from '@/views/treatment-plans/TreatmentPlanDetail.vue';
import TreatmentPlanForm from '@/views/treatment-plans/TreatmentPlanForm.vue';

const routes = [
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  // Add the new Estadisticas route
  {
    path: '/estadisticas',
    name: 'Estadisticas',
    component: () => import('@/views/Stadistics.vue'),
    meta: {
      requiresAuth: true,
      title: 'Estadísticas'
    }
  },

  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { guest: true }
  },
  
  // Patient routes
  {
    path: '/patients',
    name: 'PatientList',
    component: PatientList,
    meta: { requiresAuth: true }
  },
  {
    path: '/patients/new',
    name: 'PatientCreate',
    component: PatientForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/patients/:id',
    name: 'PatientDetail',
    component: PatientDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/patients/:id/edit',
    name: 'PatientEdit',
    component: PatientForm,
    meta: { requiresAuth: true }
  },

  {
    path: '/treatment-plans',
    name: 'TreatmentPlans',
    component: TreatmentPlanList,
    meta: { requiresAuth: true }
  },
  {
    path: '/treatment-plans/:id',
    name: 'TreatmentPlanDetail',
    component: TreatmentPlanDetail,
    meta: { requiresAuth: true },
    props: true
  },
  {
    path: '/treatment-plans/create',
    name: 'TreatmentPlanCreate',
    component: TreatmentPlanForm, 
    meta: { requiresAuth: true }
  },
  {
    path: '/treatment-plans/:id/edit',
    name: 'TreatmentPlanEdit',
    component: TreatmentPlanForm, 
    meta: { requiresAuth: true },
    props: true
  },
  
  // Appointment routes
  {
    path: '/appointments',
    name: 'AppointmentList',
    component: AppointmentList,
    meta: { requiresAuth: true }
  },
  {
    path: '/appointments/new',
    name: 'AppointmentCreate',
    component: AppointmentForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/appointments/:id',
    name: 'AppointmentDetail',
    component: AppointmentDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/appointments/:id/edit',
    name: 'AppointmentEdit',
    component: AppointmentForm,
    meta: { requiresAuth: true }
  },
  
  // Clinical history routes
  {
    path: '/clinical-histories',
    name: 'ClinicalHistoryList',
    component: ClinicalHistoryList,
    meta: { requiresAuth: true }
  },
  {
    path: '/clinical-histories/new',
    name: 'ClinicalHistoryCreate',
    component: ClinicalHistoryForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/clinical-histories/:id',
    name: 'ClinicalHistoryDetail',
    component: ClinicalHistoryDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/clinical-histories/:id/edit',
    name: 'ClinicalHistoryEdit',
    component: ClinicalHistoryForm,
    meta: { requiresAuth: true }
  },
  
  // Odontogram routes
  {
    path: '/odontograms/:id',
    name: 'OdontogramDetail',
    component: OdontogramDetail,
    meta: { requiresAuth: true }
  },
  {
    path: '/odontograms/:id/edit',
    name: 'OdontogramEdit',
    component: OdontogramForm,
    meta: { requiresAuth: true }
  },

  {
    path: '/inventory',
    name: 'Inventory',
    component: Inventory,
    meta: { requiresAuth: true }
  },
  {
    path: '/clinical-exam/new',
    name: 'clinical-exam-create',
    component: ClinicalExamForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/clinical-exam/:id/edit',
    name: 'clinical-exam-edit',
    component: ClinicalExamForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/clinical-exam/:id',
    name: 'clinical-exam-detail',
    component: ClinicalExamDetail,
    meta: { requiresAuth: true }
  },
  
  // Catch-all route for 404
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  },

];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// Navigation guard to check authentication
router.beforeEach((to, from, next) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // Check if route requires authentication
    if (!isAuthenticated) {
      // Redirect to login page if not authenticated
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      });
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.guest) && isAuthenticated) {
    // Redirect to dashboard if trying to access guest routes while authenticated
    next({ path: '/' });
  } else {
    next();
  }
});

export default router;